import { Tree, NodeType, NodeSet } from "lezer-tree";
import { tags, styleTags } from "@codemirror/next/highlight";
import { Language, defineLanguageFacet, languageDataProp, indentService, getIndentUnit, syntaxTree } from "@codemirror/next/language";
import { StringStream } from "./stringstream";
export { StringStream };
function fullParser(spec) {
    return {
        token: spec.token,
        blankLine: spec.blankLine || (() => { }),
        startState: spec.startState || (() => true),
        copyState: spec.copyState || defaultCopyState,
        indent: spec.indent || (() => null),
        languageData: spec.languageData || {}
    };
}
function defaultCopyState(state) {
    if (typeof state != "object")
        return state;
    let newState = {};
    for (let prop in state) {
        let val = state[prop];
        newState[prop] = (val instanceof Array ? val.slice() : val);
    }
    return newState;
}
/// A [language](#language.Language) class based on a streaming
/// parser.
export class StreamLanguage extends Language {
    constructor(parser) {
        let data = defineLanguageFacet(parser.languageData);
        let p = fullParser(parser);
        let startParse = (input, startPos, context) => new Parse(this, input, startPos, context);
        super(data, { startParse }, [indentService.of((cx, pos) => this.getIndent(cx, pos))]);
        this.streamParser = p;
        this.docType = docID(data);
        this.stateAfter = new WeakMap;
    }
    static define(spec) { return new StreamLanguage(spec); }
    getIndent(cx, pos) {
        let tree = syntaxTree(cx.state), at = tree.resolve(pos);
        while (at && at.type != typeArray[this.docType])
            at = at.parent;
        if (!at)
            return null;
        let start = findState(this, tree, 0, at.from, pos), statePos, state;
        if (start) {
            state = start.state;
            statePos = start.pos + 1;
        }
        else {
            state = this.streamParser.startState(cx.unit);
            statePos = 0;
        }
        if (pos - statePos > 10000 /* MaxIndentScanDist */)
            return null;
        while (statePos < pos) {
            let line = cx.state.doc.lineAt(statePos), end = Math.min(pos, line.to);
            if (line.length) {
                let stream = new StringStream(line.text, cx.state.tabSize, cx.unit);
                while (stream.pos < end - line.from)
                    readToken(this.streamParser.token, stream, state);
            }
            else {
                this.streamParser.blankLine(state, cx.unit);
            }
            if (end == pos)
                break;
            statePos = line.to + 1;
        }
        let { text } = cx.state.doc.lineAt(pos);
        return this.streamParser.indent(state, /^\s*(.*)/.exec(text)[1], cx);
    }
    get allowsNesting() { return false; }
}
function findState(lang, tree, off, startPos, before) {
    let state = off >= startPos && off + tree.length <= before && lang.stateAfter.get(tree);
    if (state)
        return { state: lang.streamParser.copyState(state), pos: off + tree.length };
    for (let i = tree.children.length - 1; i >= 0; i--) {
        let child = tree.children[i], pos = off + tree.positions[i];
        let found = child instanceof Tree && pos < before && findState(lang, child, pos, startPos, before);
        if (found)
            return found;
    }
    return null;
}
function cutTree(lang, tree, from, to, inside) {
    if (inside && from <= 0 && to >= tree.length)
        return tree;
    if (!inside && tree.type == typeArray[lang.docType])
        inside = true;
    for (let i = tree.children.length - 1; i >= 0; i--) {
        let pos = tree.positions[i] + from, child = tree.children[i], inner;
        if (pos < to && child instanceof Tree) {
            if (!(inner = cutTree(lang, child, from - pos, to - pos, inside)))
                break;
            return !inside ? inner
                : new Tree(tree.type, tree.children.slice(0, i).concat(inner), tree.positions.slice(0, i + 1), pos + inner.length);
        }
    }
    return null;
}
function findStartInFragments(lang, fragments, startPos, state) {
    for (let f of fragments) {
        let found = f.from <= startPos && f.to > startPos && findState(lang, f.tree, 0 - f.offset, startPos, f.to), tree;
        if (found && (tree = cutTree(lang, f.tree, startPos + f.offset, found.pos + f.offset, false)))
            return { state: found.state, tree };
    }
    return { state: lang.streamParser.startState(getIndentUnit(state)), tree: Tree.empty };
}
class Parse {
    constructor(lang, input, startPos, context) {
        this.lang = lang;
        this.input = input;
        this.startPos = startPos;
        this.context = context;
        this.chunks = [];
        this.chunkPos = [];
        this.chunk = [];
        let { state, tree } = findStartInFragments(lang, context.fragments, startPos, context.state);
        this.state = state;
        this.pos = this.chunkStart = startPos + tree.length;
        if (tree.length) {
            this.chunks.push(tree);
            this.chunkPos.push(startPos);
        }
        if (this.pos < context.viewport.from - 100000 /* MaxDistanceBeforeViewport */) {
            this.state = this.lang.streamParser.startState(getIndentUnit(context.state));
            context.skipUntilInView(this.pos, context.viewport.from);
            this.pos = context.viewport.from;
        }
    }
    advance() {
        let end = Math.min(this.context.viewport.to, this.chunkStart + 2048 /* ChunkSize */);
        while (this.pos < end)
            this.parseLine();
        if (this.chunkStart < this.pos)
            this.finishChunk();
        if (this.pos < this.context.viewport.to)
            return null;
        this.context.skipUntilInView(this.pos, this.input.length);
        return this.finish();
    }
    parseLine() {
        let line = this.input.lineAfter(this.pos), { streamParser } = this.lang;
        let stream = new StringStream(line, this.context ? this.context.state.tabSize : 4, getIndentUnit(this.context.state));
        if (stream.eol()) {
            streamParser.blankLine(this.state, stream.indentUnit);
        }
        else {
            while (!stream.eol()) {
                let token = readToken(streamParser.token, stream, this.state);
                if (token)
                    this.chunk.push(tokenID(token), this.pos + stream.start, this.pos + stream.pos, 4);
            }
        }
        this.pos += line.length;
        if (this.pos < this.input.length)
            this.pos++;
    }
    finishChunk() {
        let tree = Tree.build({
            buffer: this.chunk,
            start: this.chunkStart,
            length: this.pos - this.chunkStart,
            nodeSet,
            topID: 0,
            maxBufferLength: 2048 /* ChunkSize */
        });
        this.lang.stateAfter.set(tree, this.lang.streamParser.copyState(this.state));
        this.chunks.push(tree);
        this.chunkPos.push(this.chunkStart);
        this.chunk = [];
        this.chunkStart = this.pos;
    }
    finish() {
        return new Tree(typeArray[this.lang.docType], this.chunks, this.chunkPos, this.pos - this.startPos).balance();
    }
    forceFinish() {
        return this.finish();
    }
}
function readToken(token, stream, state) {
    stream.start = stream.pos;
    for (let i = 0; i < 10; i++) {
        let result = token(stream, state);
        if (stream.pos > stream.start)
            return result;
    }
    throw new Error("Stream parser failed to advance stream.");
}
const tokenTable = Object.create(null);
const typeArray = [NodeType.none];
const nodeSet = new NodeSet(typeArray);
const warned = [];
function tokenID(tag) {
    return !tag ? 0 : tokenTable[tag] || (tokenTable[tag] = createTokenType(tag));
}
for (let [legacyName, name] of [
    ["variable", "variableName"],
    ["variable-2", "variableName.special"],
    ["string-2", "string.special"],
    ["def", "variableName.definition"],
    ["tag", "typeName"],
    ["attribute", "propertyName"],
    ["type", "typeName"],
    ["builtin", "variableName.standard"],
    ["qualifier", "modifier"],
    ["error", "invalid"],
    ["header", "heading"],
    ["property", "propertyName"]
])
    tokenTable[legacyName] = tokenID(name);
function warnForPart(part, msg) {
    if (warned.indexOf(part) > -1)
        return;
    warned.push(part);
    console.warn(msg);
}
function createTokenType(tagStr) {
    let tag = null;
    for (let part of tagStr.split(".")) {
        let value = tags[part];
        if (!value) {
            warnForPart(part, `Unknown highlighting tag ${part}`);
        }
        else if (typeof value == "function") {
            if (!tag)
                warnForPart(part, `Modifier ${part} used at start of tag`);
            else
                tag = value(tag);
        }
        else {
            if (tag)
                warnForPart(part, `Tag ${part} used as modifier`);
            else
                tag = value;
        }
    }
    if (!tag)
        return 0;
    let name = tagStr.replace(/ /g, "_"), type = NodeType.define({
        id: typeArray.length,
        name,
        props: [styleTags({ [name]: tag })]
    });
    typeArray.push(type);
    return type.id;
}
function docID(data) {
    let id = typeArray.length;
    typeArray.push(new NodeType("document", languageDataProp.set(Object.create(null), data), id));
    return id;
}
